import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChartPie, 
  TrendUp, 
  TrendDown,
  ArrowRight, 
  SpinnerGap,
  Warning,
  CheckCircle,
  CurrencyDollar,
  Briefcase
} from '@phosphor-icons/react';
import { jobCostsAPI } from '../api/api';

const CostOverviewWidget = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const response = await jobCostsAPI.getContractorOverview();
      if (response.data.success) {
        setOverview(response.data.overview);
        setRecentJobs(response.data.recent_jobs || []);
      }
    } catch (error) {
      console.error('Failed to fetch cost overview:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
        <div className="flex items-center gap-2 mb-4">
          <ChartPie size={24} weight="bold" className="text-emerald-500" />
          <h3 className="text-lg font-bold text-neutral-900 dark:text-white">Cost Overview</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <SpinnerGap size={32} weight="bold" className="animate-spin text-emerald-500" />
        </div>
      </div>
    );
  }

  const hasData = overview && (overview.total_spent > 0 || overview.jobs_tracked > 0);
  const budgetHealth = overview?.total_budget > 0 
    ? ((overview.total_spent / overview.total_budget) * 100).toFixed(1)
    : 0;
  const isOverBudget = overview?.jobs_over_budget > 0;

  return (
    <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <ChartPie size={24} weight="bold" />
            </div>
            <div>
              <h3 className="text-lg font-black">Cost Overview</h3>
              <p className="text-sm text-white/80">
                {overview?.jobs_tracked || 0} jobs tracked
              </p>
            </div>
          </div>
          <button 
            onClick={() => navigate('/my-jobs')}
            className="flex items-center gap-1 text-white/90 hover:text-white font-bold text-sm"
          >
            View Jobs <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </div>

      <div className="p-5">
        {!hasData ? (
          <div className="text-center py-6">
            <CurrencyDollar size={48} weight="light" className="mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
            <p className="text-neutral-500 dark:text-neutral-400 mb-2">No costs tracked yet</p>
            <p className="text-sm text-neutral-400 dark:text-neutral-500">
              Start tracking costs on your jobs
            </p>
            <button
              onClick={() => navigate('/my-jobs')}
              className="mt-4 px-4 py-2 bg-emerald-500 text-white font-bold rounded-lg border-2 border-black shadow-[2px_2px_0px_#000] hover:shadow-[3px_3px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
            >
              Go to My Jobs
            </button>
          </div>
        ) : (
          <>
            {/* Main Stats */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border-2 border-emerald-200 dark:border-emerald-800">
                <p className="text-sm font-bold text-emerald-700 dark:text-emerald-400 mb-1">Total Spent</p>
                <p className="text-2xl font-black text-neutral-900 dark:text-white">
                  ${(overview?.total_spent || 0).toLocaleString()}
                </p>
              </div>
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border-2 border-blue-200 dark:border-blue-800">
                <p className="text-sm font-bold text-blue-700 dark:text-blue-400 mb-1">Total Budget</p>
                <p className="text-2xl font-black text-neutral-900 dark:text-white">
                  ${(overview?.total_budget || 0).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Budget Health Indicator */}
            {overview?.total_budget > 0 && (
              <div className="mb-4 p-3 rounded-xl border-2 border-neutral-200 dark:border-neutral-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-neutral-600 dark:text-neutral-400">Overall Budget Health</span>
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${
                    isOverBudget 
                      ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                      : budgetHealth > 80
                        ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                        : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                  }`}>
                    {isOverBudget ? (
                      <><Warning size={12} weight="bold" /> {overview.jobs_over_budget} Over Budget</>
                    ) : budgetHealth > 80 ? (
                      <><TrendUp size={12} weight="bold" /> {budgetHealth}% Used</>
                    ) : (
                      <><CheckCircle size={12} weight="bold" /> On Track</>
                    )}
                  </div>
                </div>
                <div className="h-3 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all ${
                      isOverBudget ? 'bg-red-500' : budgetHealth > 80 ? 'bg-amber-500' : 'bg-emerald-500'
                    }`}
                    style={{ width: `${Math.min(parseFloat(budgetHealth), 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Recent Jobs with Costs */}
            {recentJobs.length > 0 && (
              <div>
                <p className="text-xs font-bold text-neutral-500 dark:text-neutral-400 uppercase mb-2">Jobs with Costs</p>
                <div className="space-y-2">
                  {recentJobs.filter(j => j.spent > 0).slice(0, 3).map((job) => {
                    const percent = job.budget > 0 ? ((job.spent / job.budget) * 100).toFixed(0) : 0;
                    const isOver = job.budget > 0 && job.spent > job.budget;
                    
                    return (
                      <div 
                        key={job.job_id}
                        onClick={() => navigate(`/job/${job.job_id}`)}
                        className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-700 rounded-lg border-2 border-neutral-200 dark:border-neutral-600 hover:border-emerald-500 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isOver ? 'bg-red-500' : 'bg-emerald-500'
                          }`}>
                            <Briefcase size={20} weight="bold" className="text-white" />
                          </div>
                          <div>
                            <p className="font-bold text-neutral-900 dark:text-white text-sm">{job.title}</p>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400">
                              {job.budget > 0 ? `${percent}% of $${job.budget.toLocaleString()}` : 'No budget set'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`font-black ${isOver ? 'text-red-500' : 'text-neutral-900 dark:text-white'}`}>
                            ${job.spent.toLocaleString()}
                          </span>
                          {isOver && (
                            <p className="text-xs text-red-500 font-bold">Over budget</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CostOverviewWidget;
